import { Button } from '@/components/ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.js';
import { pluralize } from '@/lib/string-utils.js';
import type { CoachPoolWithMatchCount } from '@airreading/common/data';

interface RemoveCoachPoolDialogProps {
  pool: CoachPoolWithMatchCount | null;
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

function RemovalImpactStat({ count, label }: { count: number; label: string }) {
  return (
    <p className="rounded-md bg-muted px-4 py-4">
      <span className="block text-3xl font-semibold text-foreground">{count}</span>
      <span className="mt-1 block text-sm text-muted-foreground">{label}</span>
    </p>
  );
}

// Dialog to confirm removing a coach pool
export const RemoveCoachPoolDialog = ({ pool, isPending, onOpenChange, onConfirm }: RemoveCoachPoolDialogProps) => {
  const dataTestId = pool ? 'teacher-pool-modal-remove' : undefined;
  const teacherLabel = pluralize(pool?.matchingTeachersCount ?? 0, 'teacher', 'teachers');
  const matchVerb = pool?.matchingTeachersCount === 1 ? 'matches' : 'match';

  return (
    <Dialog open={pool != null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl" data-testid={dataTestId}>
        {pool && (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl">Remove {pool.title}?</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-4">
              <DialogDescription>
                This will remove the pool and its saved filters. This cannot be undone. Teacher records will not be
                deleted.
              </DialogDescription>
              <p className="text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">
                  {pool.matchingTeachersCount} {teacherLabel}
                </span>{' '}
                currently {matchVerb} this {pool.isActive ? 'active' : 'inactive'} pool.
              </p>
              {pool.isActive ? (
                <div className="grid grid-cols-2 gap-3">
                  <RemovalImpactStat count={pool.returningToNormalPriorityCount} label="return to normal priority" />
                  <RemovalImpactStat
                    count={pool.remainingReservedCount}
                    label="remain reserved through other active pools"
                  />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  This pool is inactive, so removing it will not change any teacher&apos;s reservation status.
                </p>
              )}
              <p className="text-sm text-muted-foreground">
                Other pools, existing assignments, and already-started runs are unchanged.
              </p>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={isPending}
                data-testid={dataTestId ? `${dataTestId}-button-cancel` : undefined}
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="outline"
                className="border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive"
                loading={isPending}
                data-testid={dataTestId ? `${dataTestId}-button-submit` : undefined}
                onClick={onConfirm}
              >
                Remove pool
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
